import { get, getUseGetQueryKey, useGetQuery, type UseGetQueryOptions } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { type UseQueryResult } from "@tanstack/react-query";
import qs from "qs";
import { z } from "zod";

import { type QualificationsResponse, qualificationsResponseSchema } from "../types";

// FIXME: Automatically disabled during ESLint config update
const getQualificationsQuerySchema = z.object({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  filter: z
    .object({
      workplaceId: z.string().optional(),
      enabled: z.boolean().optional(),
    })
    .optional(),
});

export type GetQualificationsParams = z.infer<typeof getQualificationsQuerySchema>;

export interface UseGetQualificationsOptions extends UseGetQueryOptions<QualificationsResponse> {
  customHeaders?: Record<string, string>;
}

const GET_QUALIFICATIONS_URL = `${environmentConfig.REACT_APP_BASE_API_URL}/qualifications`;

function serializeGetQualificationsParams(params: Record<string, unknown>) {
  return qs.stringify(params, { arrayFormat: "comma" });
}

export function createGetQualificationsQueryOptions(params: GetQualificationsParams = {}) {
  return {
    queryKey: getUseGetQueryKey(GET_QUALIFICATIONS_URL, params),
    queryFn: async () => {
      const response = await get({
        url: GET_QUALIFICATIONS_URL,
        requestSchema: getQualificationsQuerySchema,
        responseSchema: qualificationsResponseSchema,
        queryParams: params,
        paramsSerializer: serializeGetQualificationsParams,
      });

      return response.data;
    },
    staleTime: 60_000,
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_QUALIFICATIONS_FAILURE,
      userErrorMessage: "Failed to get qualifications",
    },
  };
}

export function useGetQualifications(
  params: GetQualificationsParams = {},
  options: UseGetQualificationsOptions = {}
): UseQueryResult<QualificationsResponse> {
  return useGetQuery({
    url: GET_QUALIFICATIONS_URL,
    requestSchema: getQualificationsQuerySchema,
    responseSchema: qualificationsResponseSchema,
    queryParams: params,
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_QUALIFICATIONS_FAILURE,
      userErrorMessage: "Failed to get qualifications",
    },
    // FIXME: Automatically disabled during ESLint config update
    paramsSerializer: serializeGetQualificationsParams,
    ...options,
  });
}
